export const stepOneData = [
  {
    heading: "Understanding EMI and Its Importance",
    // paras before heading
    paraOne:
      "An Equated Monthly Installment (EMI) is the fixed amount you pay monthly towards repaying your loan. It comprises both the principal and interest components. Estimating your EMI in advance helps in budgeting and ensures that the loan remains affordable throughout its tenure.",

    impactHead: "Impact of Loan Tenure on EMI and Total Repayment",

    infoList: [
      {
        heading: "Shorter Tenure: ",
        description:
          "Leads to higher EMIs but reduces the total interest payable.",
      },
      {
        heading: "Longer Tenure: ",
        description:
          " Results in lower EMIs but increases the total interest paid over time.",
      },
    ],
    example: [
      {
        heading: "Loan Amount: ",
        content: "₹5,00,000",
      },
      {
        heading: "Interest Rate: ",
        content: "12% per annum",
      },
    ],

    tablecontent: {
      heading: [
        {
          head: "Tenure",
        },
        {
          head: "EMI (₹)",
        },
        {
          head: "Total Interest (₹)",
        },
        {
          head: "Total Repayment (₹)",
        },
      ],
      row: [
        {
          tenure: "3 years",
          emi: "16,607",
          interest: "99,852",
          repayment: "5,99,852",
        },
        {
          tenure: "5 years",
          emi: "11,122",
          interest: "1,67,320",
          repayment: "6,67,320",
        },
      ],
    },

    tip: "Always ensure that your EMI does not exceed 40-50% of your monthly income to maintain financial stability.",
  },
];

export const stepTwoData = [
  {
    heading: "Why Consider APR Over Just Interest Rates?",
    subtitle:
      "The Annual Percentage Rate (APR) provides a comprehensive view of the loan’s cost by including both the interest rate and additional fees like processing charges. This helps in comparing different loan offers more effectively.",

    tip: " A loan with a slightly higher interest rate but lower processing fees might have a lower APR, making it more cost-effective in the long run.",

    paraOne:
      "Don’t see what you’re looking for? NerdWallet has a long list of mortgage calculators to help you make whatever financial decision comes your way.",

    personalLoanList: [
      {
        heading: "Personal loan calculator: ",
        info: "Estimate what you’d pay monthly on a loan.",
      },
      {
        heading: "Debt-to-income ratio calculator:",
        info: "Find out how your current debt payments compare to your income.",
      },
      {
        heading: "Debt consolidation calculator: ",
        info: " Could consolidating debt save you money on interest?",
      },
      {
        heading: "Personal loan refinance calculator: ",
        info: "Consider whether to refinance an existing loan.",
      },
    ],
    autoLoanList: [
      {
        heading: "Auto loan calculator:",
        info: "See current average auto loan rates and calculate a payment.",
      },
      {
        heading: "Reverse auto loan calculator:",
        info: "Calculate how much car your payment can buy.",
      },
      {
        heading: "How much car can I afford? ",
        info: "Get a starting point on a budget for car shopping.",
      },
      {
        heading: "Auto loan amortization calculator: ",
        info: "How much will you owe a year from now?",
      },
      {
        heading: "Auto loan refinancing calculator:",
        info: "What will payments look like if you can find a cheaper loan?",
      },
      {
        heading: "How much should my car payment be? ",
        info: "What's an affordable monthly note for your income?",
      },
      {
        heading: "Auto lease calculator: ",
        info: "Consider the lease term, security deposits and the money factor.",
      },
    ],

    studentLoanList: [
      {
        heading: "Student loan payment calculator:",
        info: "Add up your loans and calculate a monthly payment.",
      },
      {
        heading: "Student loan refinancing calculator:",
        info: "Estimate savings from refinancing your student loans.",
      },
      {
        heading: "Student loan payoff calculator:  ",
        info: "See what extra payments or a lower interest rate can do.",
      },
      {
        heading: "Weighted average interest rate calculator: ",
        info: "Calculate the combined rate on your student loans.",
      },
      {
        heading: "Student loan consolidation calculator:",
        info: "A new payment if you consolidate federal loans.",
      },
      {
        heading: "Discretionary income calculator: ",
        info: "Figure out your payment under income-driven plans.",
      },
      {
        heading: "Daily student loan interest calculator:  ",
        info: "What your loans cost, per day.",
      },
    ],
  },
];

export const stepThreeData = [
  {
    heading: "Factors Influencing Your EMI and Total Repayment",

    factorsList: [
      {
        heading: "Processing Fees: ",
        info: "High processing fees can increase your overall loan cost. Always check if the processing fee is a flat amount or a percentage of the loan.",
      },
      {
        heading: "Prepayment Charges: ",
        info: "Some lenders levy fees for early repayment. Understand these charges before opting for prepayment.",
      },
      {
        heading: "Late Payment Penalties: ",
        info: "Missing EMI payments can attract penalties and affect your credit score. Ensure timely payments to avoid additional costs.",
      },
    ],
    tip: "Before finalizing a loan, inquire about all associated fees and charges to avoid unexpected expenses.",
  },
];

export const stepFourData = [
  {
    heading: "The Importance of Interest Rates in Personal Loans",
    subtitle:
      "The interest rate directly impacts your EMI and the total cost of the loan. A lower interest rate reduces both your monthly payments and the overall amount payable.",

    negotiatingHead: "Negotiating Interest Rates:",
    list: [
      {
        head: "Credit Score: ",
        info: "A higher credit score indicates good creditworthiness, which can be leveraged to negotiate lower interest rates.",
      },
      {
        head: "Existing Relationship with the Bank: ",
        info: "Banks often offer preferential rates to existing customers. If you have a savings account or other products with the bank, use this relationship to negotiate better terms.",
      },
      {
        head: "Market Research: ",
        info: "Compare offers from multiple lenders. Use these comparisons as a basis for negotiation.",
      },
    ],
    tip: "Regularly monitor your credit score and maintain a good credit history to enhance your bargaining power with lenders.",
  },
];
export const stepFiveData = [
  {
    heading: "How Credit Score Affects Interest Rates and EMI",
    subtitle:
      "Your credit score is a numerical representation of your creditworthiness. Lenders use it to assess the risk of lending to you.",

    creditScoreList: [
      {
        head: "High Credit Score (750 and above): ",
        info: "Indicates low risk, leading to lower interest rates and EMIs.",
      },
      {
        head: "Moderate Credit Score (650-749): ",
        info: "May result in standard interest rates.",
      },
      {
        head: "Low Credit Score (below 650): ",
        info: "Considered high risk, leading to higher interest rates and EMIs.",
      },
    ],

    improvingCredit: "Improving Your Credit Score:",
    improveCreditlist: [
      {
        head: "Timely Payments: ",
        info: "Always pay your EMIs and credit card bills on time.",
      },
      {
        head: "Credit Utilization Ratio: ",
        info: "Keep your credit card usage below 30% of the limit.",
      },
      {
        head: "Avoid Multiple Loan Applications: ",
        info: "Too many loan inquiries can negatively impact your score.",
      },
    ],
    tip: "Regularly check your credit report for errors and get them rectified to maintain an accurate credit score.",
  },
];
